import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import { listCacheFallback } from '@/lib/list-cache-fallback';
import type {
  PurchaseDocument,
  PurchaseDocumentFilters,
  CreatePurchaseDocumentInput,
  UpdatePurchaseDocumentInput,
} from '@erp/data-access/interfaces';

const PURCHASES_KEY = ['purchases'] as const;
const OFFLINE_PREFIX = 'OFFLINE-';

/**
 * Always fetches the FULL document list (canonical key) and filters in
 * memory. Same offline-first pattern as useSalesDocuments — every call
 * site reads the prewarmed cache, no per-filter network round-trip.
 */
export function usePurchaseDocuments(filters?: PurchaseDocumentFilters) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...PURCHASES_KEY, filters],
    queryFn: () => ds.purchases.listDocuments(filters),
    placeholderData: keepPreviousData,
  });
}

export function usePurchaseDocumentCount(filters?: PurchaseDocumentFilters) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...PURCHASES_KEY, 'count', filters],
    queryFn: () => ds.purchases.countDocuments(filters),
    placeholderData: keepPreviousData,
  });
}

export function usePurchaseDocument(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...PURCHASES_KEY, id],
    queryFn: () => ds.purchases.getDocumentById(id),
    enabled: !!id,
    placeholderData: () => listCacheFallback<PurchaseDocument>(qc, PURCHASES_KEY, id),
  });
}

function optimisticPurchaseDocument(
  input: CreatePurchaseDocumentInput,
  clientId: string,
): PurchaseDocument {
  return {
    id: clientId,
    docType: input.docType,
    number: OFFLINE_PREFIX + clientId.slice(0, 6).toUpperCase(),
    supplierId: input.supplierId,
    docDate: input.docDate ?? new Date().toISOString().slice(0, 10),
    parentDocId: input.parentDocId ?? null,
    isInvoiced: false,
    notes: input.notes ?? null,
    totalHt: input.totalHt ?? 0,
    totalVat: input.totalVat ?? 0,
    totalTtc: input.totalTtc ?? 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  } as unknown as PurchaseDocument;
}

export function useCreatePurchaseDocument() {
  const ds = useDataService();
  return useResilientMutation<CreatePurchaseDocumentInput, PurchaseDocument>({
    entity: 'purchase_documents',
    op: 'create',
    invalidate: [PURCHASES_KEY],
    online: (input) => ds.purchases.createDocument(input),
    optimistic: optimisticPurchaseDocument,
    applyOptimistic: (qc, doc) => {
      qc.setQueriesData<PurchaseDocument[]>({ queryKey: PURCHASES_KEY }, (old) =>
        Array.isArray(old) && !old.some((d) => d.id === doc.id) ? [doc, ...old] : old,
      );
    },
  });
}

export function useUpdatePurchaseDocument() {
  const ds = useDataService();
  return useResilientMutation<{ id: string; input: UpdatePurchaseDocumentInput }, PurchaseDocument>({
    entity: 'purchase_documents',
    op: 'update',
    invalidate: [PURCHASES_KEY],
    online: ({ id, input }) => ds.purchases.updateDocument(id, input),
    optimistic: ({ id, input }) =>
      ({
        id,
        ...input,
        updatedAt: new Date().toISOString(),
      }) as unknown as PurchaseDocument,
    applyOptimistic: (qc, _r, { id, input }) => {
      qc.setQueriesData<PurchaseDocument[]>({ queryKey: PURCHASES_KEY }, (old) =>
        Array.isArray(old)
          ? old.map((d) => (d.id === id ? ({ ...d, ...input } as unknown as PurchaseDocument) : d))
          : old,
      );
    },
  });
}

export function useDeletePurchaseDocument() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'purchase_documents',
    op: 'delete',
    invalidate: [PURCHASES_KEY],
    online: (id) => ds.purchases.deleteDocument(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, id) => {
      qc.setQueriesData<PurchaseDocument[]>({ queryKey: PURCHASES_KEY }, (old) =>
        Array.isArray(old) ? old.filter((d) => d.id !== id) : old,
      );
    },
  });
}

export function useMarkPurchaseAsInvoiced() {
  const ds = useDataService();
  // markAsInvoiced returns void on the server; we synthesize a stub
  // PurchaseDocument so the optimistic + cache types stay aligned.
  return useResilientMutation<string, PurchaseDocument>({
    entity: 'purchase_documents',
    op: 'custom',
    method: 'markAsInvoiced',
    invalidate: [PURCHASES_KEY],
    online: (id) => ds.purchases.markAsInvoiced(id) as unknown as Promise<PurchaseDocument>,
    optimistic: (id) => ({ id, isInvoiced: true } as unknown as PurchaseDocument),
    applyOptimistic: (qc, _r, id) => {
      qc.setQueriesData<PurchaseDocument[]>({ queryKey: PURCHASES_KEY }, (old) =>
        Array.isArray(old) ? old.map((d) => (d.id === id ? { ...d, isInvoiced: true } : d)) : old,
      );
    },
  });
}

export function usePurchaseChildDocuments(parentId: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...PURCHASES_KEY, 'children', parentId],
    queryFn: () => ds.purchases.getChildDocuments(parentId),
    enabled: !!parentId,
  });
}
